import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { useCallback, useEffect } from 'react';
import { ChatDialogEvent, HoldStore, InvolveStore } from 'sdk';

import { useSDK } from '../../app';
import { AgentMessageContainer } from '../chat/chatMessagesList/agentMessage';
import { useMessageField } from '../forms/fields/messageField/common';
import { MessageFieldInvolve } from '../forms/fields/messageField/involve';

type Interface = {
	message: ChatDialogEvent;
	store: HoldStore | InvolveStore;
};

const AGENT_MESSAGE_PADDING = { padding: '10px 16px' } as const;

const Component = ({ message, store }: Interface): ReactElement | null => {
	const sdk = useSDK();
	const [value, onChange] = useMessageField();

	useEffect(() => {
		if (message.accountId !== undefined) {
			sdk.fetchAccountInfo(message.accountId);
		}
	}, [sdk, message]);

	useEffect(() => {
		store.pauseInteraction();
		return (): void => {
			store.disableActiveInteraction();
			store instanceof HoldStore && store.setIsTriggered(false);
			if (store.allowToResume) {
				store.resume();
			}
		};
	}, [store]);

	const onSendClick = useCallback(async () => {
		await sdk.startNewConversation(
			{ text: value },
			{
				involveEvent: {
					siteScenarioId: store.activeInteraction?.siteSettings.id as number,
					creationTimestamp: message.creationTimestamp as string,
					text: message.view.text as string,
				},
			},
			true
		);
		sdk.replyMessageStore.clearRepliedMessage();
		if (sdk.startNewConversationRequestStatus.isLoaded) {
			sdk.uiStateStore.setOpened(true);
		}
	}, [sdk, value, store.activeInteraction?.siteSettings.id, message]);

	return (
		<>
			<div
				css={css`
					div& {
						${AGENT_MESSAGE_PADDING}
					}
				`}
			>
				<AgentMessageContainer message={message} isInvolve />
			</div>
			<MessageFieldInvolve
				onChange={onChange}
				value={value}
				disabled={
					sdk.startNewConversationRequestStatus.isPending || sdk.newConversationHistoryRequestStatus.isPending
				}
				onSendClick={onSendClick}
			/>
		</>
	);
};

const Involve = observer(Component);

export { Involve };
